import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  LinearProgress,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  TextField,
  IconButton,
  FormControlLabel
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Check as CheckIcon,
  SkipNext as SkipNextIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { SIZES } from '../data/constants';
import './ListingAdmin.css';
import databaseService from '../services/databaseService';
import SvgPathSection from './SvgPathSection';
import SizesSection from './SizesSection';
import SimpleFileBrowser from './SimpleFileBrowser';

import logger from '../utils/logger.js';
// Create dark theme with proper WCAG contrast
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#64b5f6', // Better contrast blue
    },
    secondary: {
      main: '#f06292', // Better contrast pink
    },
    background: {
      default: '#0d1117', // Darker for better contrast
      paper: '#161b22',   // Darker paper background
    },
    text: {
      primary: '#f0f6fc',    // High contrast white
      secondary: '#8b949e',  // Better contrast gray
    },
  },
});

const SvgPathAutoAssociation = () => {
  const [currentListing, setCurrentListing] = useState(null);
  const [stickerData, setStickerData] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [extractedVariations, setExtractedVariations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ total: 0, completed: 0, skipped: 0 });
  const [suggestedSvgPath, setSuggestedSvgPath] = useState('');
  const [alternateSuggestions, setAlternateSuggestions] = useState([]);
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [filePickerTarget, setFilePickerTarget] = useState({ type: 'main', index: null });
  const [error, setError] = useState('');

  // Initialize sizes array
  useEffect(() => {
    const initialSizes = SIZES.map(size => ({
      name: size.name,
      width: '',
      height: '',
      price: '',
      sku: '',
      enabled: false
    }));
    setSizes(initialSizes);
  }, []);

  // Load next listing to process
  const loadNextListing = async () => {
    try {
      setLoading(true);
      setError('');

      // Get listings missing SVG paths, sorted by bestseller status
      const listings = await databaseService.getUnifiedStickers({
        hasSvgPath: false,
        sortBy: 'sales',
        sortOrder: 'desc',
        limit: 999 // Maximum allowed by validation (triggers unlimited mode)
      });

      if (!listings.data || listings.data.length === 0) {
        setError('No more listings to process! All listings have SVG paths.');
        setLoading(false);
        return;
      }

      // Load skipped IDs from localStorage and filter them out
      let skippedIds = [];
      try {
        const lsSkips = localStorage.getItem('autoAssocSkipped');
        if (lsSkips) skippedIds = JSON.parse(lsSkips);
      } catch (_) {}
      const skippedSet = new Set((skippedIds || []).map(id => String(id)));

      const items = (listings.data || []).filter(l => !skippedSet.has(String(l.etsyListingId)));
      if (!items || items.length === 0) {
        setError('No more listings to process! All remaining are skipped or completed.');
        setLoading(false);
        return;
      }

      // Get progress from database
      const progressData = await getProgress();
      let nextIndex = 0;

      if (progressData.last_processed_listing_id) {
        // Find the next listing after the last processed one
        const lastIndex = items.findIndex(
          listing => listing.etsyListingId === progressData.last_processed_listing_id
        );
        nextIndex = lastIndex + 1;
        
        // If we've processed all listings in this batch, get the next batch
        if (nextIndex >= items.length) {
          nextIndex = 0;
        }
      }

      const listing = items[nextIndex];
      if (!listing) {
        setError('No more listings to process in current batch.');
        setLoading(false);
        return;
      }

      // Load full listing data
      const fullListingData = await databaseService.getEtsyListingData(listing.etsyListingId);
      if (!fullListingData) {
        setError(`Failed to load listing data for ${listing.etsyListingId}`);
        setLoading(false);
        return;
      }

      // Set current listing data
      setCurrentListing({
        ...listing,
        ...fullListingData
      });

      // Set sticker data
      setStickerData({
        svgPath: listing.svgPath || '',
        dimensions: listing.dimensions || {},
        sizes: listing.sizes || [],
        category: listing.category || ''
      });

      // Load sizes data
      if (listing.sizes) {
        try {
          const savedSizes = typeof listing.sizes === 'string' ? JSON.parse(listing.sizes) : listing.sizes;
          if (Array.isArray(savedSizes)) {
            const updatedSizes = SIZES.map(size => {
              const savedSize = savedSizes.find(s => s.name === size.name);
              return savedSize ? { ...size, ...savedSize } : size;
            });
            setSizes(updatedSizes);
          }
        } catch (error) {
          console.error('Error parsing saved sizes:', error);
        }
      }

              // Set extracted variations
        const variations = {
          sizes: fullListingData.available_sizes || [],
          colors: fullListingData.available_colors || []
        };
        
        logger.log('Extracted variations for listing:', {
          title: listing.title,
          listingId: listing.etsyListingId,
          available_sizes: fullListingData.available_sizes,
          available_colors: fullListingData.available_colors,
          inventory_matrix: fullListingData.inventory_matrix,
          fullListingData: fullListingData
        });
        
        setExtractedVariations(variations);
        
        // Update sizes state to enable only the available sizes from extractedVariations
        if (fullListingData.available_sizes && fullListingData.available_sizes.length > 0) {
          const updatedSizes = SIZES.map(size => ({
            ...size,
            enabled: fullListingData.available_sizes.includes(size.name),
            // If we have dimensions data, populate width/height
            width: fullListingData.dimensions?.[size.name]?.width?.toString() || '',
            height: fullListingData.dimensions?.[size.name]?.height?.toString() || ''
          }));
          
          logger.log('Updated sizes with extracted data:', updatedSizes);
          setSizes(updatedSizes);
        }

      // Generate SVG path suggestions (top 3)
      logger.log('Generating suggestion for:', listing.title);
      const suggestions = await generateSvgPathSuggestions(listing, fullListingData);
      logger.log('Generated suggestions:', suggestions);
      setAlternateSuggestions(suggestions);
      setSuggestedSvgPath(suggestions[0]?.path || '');

      // Update progress
      setProgress({
        total: items.length,
        completed: progressData.completed || 0,
        skipped: progressData.skipped || 0
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading next listing:', error);
      setError(`Error loading listing: ${error.message}`);
      setLoading(false);
    }
  };

  // Generate top SVG path suggestions based on listing data
  const generateSvgPathSuggestions = async (listing, fullData) => {
    if (!listing.category) return '';

    // Map category to folder name
    const categoryToFolder = {
      'Animal Stickers': 'animals',
      'Cryptids': 'cryptids',
      'Dinosaur Stickers': 'dinosaurs',
      'Horror': 'horror',
      'Offroad Slogans': 'offroad',
      // FIX: Real folder name for outdoor content is 'beach'
      'Outdoor Stickers': 'beach',
      'Police/Military/Fire': 'police-military',
      'Pop Culture Stickers': 'pop',
      'Space Stickers': 'space',
      'SVGs': 'svgs',
      'Vehicle Stickers': 'vehicles',
      // Vehicles pop-culture assets live under the shared 'pop' folder
      'Vehicles Pop Culture': 'pop'
    };

    const folder = categoryToFolder[listing.category];
    if (!folder) return '';

    try {
      // Get real files from the file system
      const response = await fetch('/.netlify/functions/list-files');
      const data = await response.json();
      
      if (!data.files || !Array.isArray(data.files)) {
        logger.log('No files found or invalid response');
        return '';
      }

      // Filter files to only include the relevant category folder
      const categoryFiles = data.files.filter(file => 
        file.path.includes(`/assets/images/${folder}/`)
      );

      if (categoryFiles.length === 0) {
        logger.log(`No files found in ${folder} category`);
        return '';
      }

      // Extract key words from title for matching
      const title = listing.title || '';
      const stopWords = new Set([
        'the','and','for','with','your','you','our','vinyl','decal','sticker',
        'window','windshield','micro','easter','egg','set','pack','size','sizes',
        'true','truescale','collection','corner','inside','outside','laptop',
        'hydroflask','fun','join','add','design','piece','pieces','reverse','flip',
        'drivers','side','please','note','smaller','larger','version','make','made',
        'from','high','quality','fade','resistant','can','will','approx','approximately',
        'car','truck','vehicle','vehicles','monster','cryptid','cryptids','category'
      ]);
      const words = title.toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(w => w && w.length > 2 && !stopWords.has(w));
      const wordSet = new Set(words);

      // Score files based on how well they match the title words
      const scoredFiles = categoryFiles.map(file => {
        let score = 0;
        const fileName = file.name.toLowerCase().replace(/\.(png|jpg|jpeg|svg|gif)$/i, '');
        const fileTokens = fileName.split(/[^a-z0-9]+/).filter(Boolean);
        const fileTokenSet = new Set(fileTokens);
        
        // Check each important word in the title against the filename
        words.forEach((word) => {
          // Exact token match (e.g., 'wendigo' appears as its own token)
          if (fileTokenSet.has(word)) {
            score += 200; // Highest priority
            return;
          }
          // Substring match within filename
          if (fileName.includes(word)) {
            score += 40; // Strong boost
            if (word.length >= 5) score += 20; // Longer distinctive words get extra
          }
        });

        // Bonus: filename made primarily of title tokens
        const matchedTokenCount = [...wordSet].filter(w => fileTokenSet.has(w)).length;
        if (matchedTokenCount >= 2) score += matchedTokenCount * 15;

        // Bonus for distinctive keywords
        const distinctiveWords = ['batman', 'robin', 'superman', 'spiderman', 'ironman', 'captain', 'avengers', 'star', 'wars', 'darth', 'vader', 'luke', 'han', 'chewbacca', 'marvel', 'dc', 'comics', 'superhero', 'hero', 'villain', 'dragon', 'dinosaur', 't-rex', 'raptor', 'bronto', 'stego', 'ghost', 'zombie', 'vampire', 'werewolf', 'monster', 'creature', 'tank', 'helicopter', 'plane', 'car', 'truck', 'motorcycle', 'skull', 'cross', 'angel', 'demon', 'devil'];
        
        distinctiveWords.forEach(distinctiveWord => {
          if (fileName.includes(distinctiveWord)) {
            score += 20; // Distinctive words get bonus
          }
        });

        return { file, score };
      });

      // Sort by score (highest first) and return the top 3
      scoredFiles.sort((a, b) => b.score - a.score);
      const top = scoredFiles
        .filter(s => s.score > 0)
        .slice(0, 3)
        .map(s => ({ path: s.file.path, name: s.file.name, score: s.score }));
      return top;

    } catch (error) {
      console.error('Error scanning files for suggestion:', error);
      return [];
    }
  };

  // Get progress from database
  const getProgress = async () => {
    try {
      const progressData = await databaseService.getAutoAssociationProgress();
      if (progressData && progressData.auto_association_progress) {
        try {
          const parsed = JSON.parse(progressData.auto_association_progress);
          return {
            last_processed_listing_id: progressData.last_processed_listing_id,
            completed: parsed.completed || 0,
            skipped: parsed.skipped || 0
          };
        } catch (e) {
          logger.warn('Failed to parse progress JSON:', e);
        }
      }
      return { last_processed_listing_id: null, completed: 0, skipped: 0 };
    } catch (error) {
      console.error('Error getting progress:', error);
      return { last_processed_listing_id: null, completed: 0, skipped: 0 };
    }
  };

  // Update progress in database
  const updateProgress = async (listingId, action) => {
    try {
      const currentProgress = await getProgress();
      let newCompleted = currentProgress.completed;
      let newSkipped = currentProgress.skipped;

      if (action === 'completed') {
        newCompleted++;
      } else if (action === 'skipped') {
        newSkipped++;
      }

      const progressData = {
        last_processed_listing_id: listingId,
        auto_association_progress: {
          total_listings: progress.total,
          completed: newCompleted,
          skipped: newSkipped,
          last_updated: new Date().toISOString()
        }
      };

      await databaseService.updateAutoAssociationProgress(progressData);
      
      // Update local progress state
      setProgress(prev => ({
        ...prev,
        completed: newCompleted,
        skipped: newSkipped
      }));
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  // Handle SVG path save
  const handleSvgPathSave = async () => {
    if (!currentListing || !stickerData) return;

    try {
      const result = await databaseService.updateSticker(currentListing.etsyListingId, {
        etsyListingId: String(currentListing.etsyListingId),
        ...stickerData,
        svgPath: stickerData.svgPath
      });

      if (result) {
        logger.log('SVG path saved successfully');
        // Move to next listing
        await handleNext();
      } else {
        setError('Failed to save SVG path');
      }
    } catch (error) {
      console.error('Error saving SVG path:', error);
      setError(`Error saving SVG path: ${error.message}`);
    }
  };

  // Handle sizes save
  const handleSizesSave = async () => {
    if (!currentListing || !stickerData) return;

    try {
      // Convert sizes array to dimensions format
      const dimensions = {};
      sizes.forEach(size => {
        if (size.enabled && size.width && size.height) {
          dimensions[size.name] = {
            width: parseFloat(size.width),
            height: parseFloat(size.height),
            unit: 'inches',
            source: 'manual'
          };
        }
      });

      const result = await databaseService.updateSticker(currentListing.etsyListingId, {
        etsyListingId: String(currentListing.etsyListingId),
        ...stickerData,
        sizes: sizes,
        dimensions: dimensions
      });

      if (result) {
        logger.log('Sizes saved successfully');
      } else {
        setError('Failed to save sizes');
      }
    } catch (error) {
      console.error('Error saving sizes:', error);
      setError(`Error saving sizes: ${error.message}`);
    }
  };

  // Handle next listing
  const handleNext = async () => {
    if (currentListing) {
      await updateProgress(currentListing.etsyListingId, 'completed');
    }
    await loadNextListing();
  };

  // Handle skip listing
  const handleSkip = async () => {
    if (currentListing) {
      // Persist skip locally so it won't show again in this run
      try {
        const lsSkips = localStorage.getItem('autoAssocSkipped');
        const arr = lsSkips ? JSON.parse(lsSkips) : [];
        const id = String(currentListing.etsyListingId);
        if (!arr.includes(id)) {
          arr.push(id);
          localStorage.setItem('autoAssocSkipped', JSON.stringify(arr));
        }
      } catch (_) {}
      await updateProgress(String(currentListing.etsyListingId), 'skipped');
    }
    await loadNextListing();
  };

  // Handle previous listing
  const handlePrevious = async () => {
    // This would need more complex logic to track previous listings
    // For now, just reload the current one
    if (currentListing) {
      await loadNextListing();
    }
  };

  // Load first listing on component mount
  useEffect(() => {
    loadNextListing();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Container maxWidth={false} sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom>
            SVG Path Auto-Association
          </Typography>
          <LinearProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading next listing...
          </Typography>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Container maxWidth={false} sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom>
            SVG Path Auto-Association
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={loadNextListing}>
            Try Again
          </Button>
        </Container>
      </ThemeProvider>
    );
  }

  if (!currentListing) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Container maxWidth={false} sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom>
            SVG Path Auto-Association
          </Typography>
          <Typography variant="body1">
            No listings to process.
          </Typography>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth={false} sx={{ py: 2 }}>
        {/* SHORT TOP - 2 columns in 1 row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Column 1: Truncated title + hero image */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, backgroundColor: '#21262d', border: '1px solid #30363d', height: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1.2, mb: 1 }}>
                    {currentListing?.title || 'Loading...'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#8b949e' }}>
                    ID: {currentListing?.etsyListingId || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 120,
                  height: 90,
                  border: '1px solid #30363d',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#0d1117',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <img
                    src={currentListing?.first_image_url || currentListing?.largest_size_image_url || currentListing?.images_data?.[0]?.url_fullxfull || currentListing?.images_data?.[0]?.url_570xN}
                    alt={currentListing?.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Column 2: 3 suggested images with LIGHT BACKGROUNDS */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', height: '100%' }}>
              <Typography variant="h6" sx={{ color: '#212529', fontWeight: 600, mb: 2 }}>
                Suggested Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {alternateSuggestions && alternateSuggestions.length > 0 ? (
                  alternateSuggestions.slice(0, 3).map((sug, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 100,
                        height: 75,
                        border: sug.path === stickerData?.svgPath ? '3px solid #007bff' : '2px solid #ced4da',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: '#ffffff',
                        transition: 'border-color 0.2s ease',
                        '&:hover': {
                          borderColor: '#007bff'
                        }
                      }}
                      title={`${sug.name}`}
                      onClick={() => {
                        setSuggestedSvgPath(sug.path);
                        setStickerData(prev => ({ ...(prev || {}), svgPath: sug.path }));
                      }}
                    >
                      <img
                        src={sug.path}
                        alt={sug.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', background: 'transparent' }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                    No suggestions available
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Set Management Section */}
        <Accordion defaultExpanded={false} sx={{ mb: 3, backgroundColor: '#21262d', border: '1px solid #30363d' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#21262d' }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc' }}>Set Management</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: '#21262d' }}>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stickerData?.isSet || false}
                    onChange={(e) => {
                      if (stickerData) {
                        setStickerData({
                          ...stickerData,
                          isSet: e.target.checked
                        });
                      }
                    }}
                    sx={{
                      color: '#8b949e',
                      '&.Mui-checked': {
                        color: '#64b5f6'
                      }
                    }}
                  />
                }
                label={<Typography sx={{ color: '#f0f6fc' }}>This is a SET listing (multiple designs or multiple copies)</Typography>}
              />
            </Box>

            {stickerData?.isSet && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: '#f0f6fc' }}>
                    Set Configuration
                  </Typography>

                  <TextField
                    fullWidth
                    label="Set Quantity"
                    type="number"
                    value={stickerData?.setQuantity || 1}
                    onChange={(e) => {
                      if (stickerData) {
                        const newQuantity = parseInt(e.target.value) || 1;
                        const currentPaths = stickerData.svgPaths || [];
                        const currentTitles = stickerData.svgTitles || [];

                        // For set quantity N, we need N-1 additional paths (since Design 1 uses main svgPath)
                        const additionalPathsNeeded = Math.max(0, newQuantity - 1);

                        // Ensure we have enough paths and titles for additional designs
                        const newPaths = [...currentPaths];
                        const newTitles = [...currentTitles];

                        while (newPaths.length < additionalPathsNeeded) {
                          newPaths.push('');
                          newTitles.push('');
                        }

                        // Trim if reducing quantity
                        if (newPaths.length > additionalPathsNeeded) {
                          newPaths.splice(additionalPathsNeeded);
                          newTitles.splice(additionalPathsNeeded);
                        }

                        setStickerData({
                          ...stickerData,
                          setQuantity: newQuantity,
                          svgPaths: newPaths,
                          svgTitles: newTitles
                        });
                      }
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiInputLabel-root': { color: '#8b949e' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#30363d' },
                        '&:hover fieldset': { borderColor: '#64b5f6' },
                        '&.Mui-focused fieldset': { borderColor: '#64b5f6' }
                      },
                      '& .MuiInputBase-input': { color: '#f0f6fc' }
                    }}
                  />
                </Box>

                <Typography variant="subtitle1" sx={{ mb: 2, color: '#f0f6fc' }}>
                  SVG Paths for Set Designs
                </Typography>
                <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mb: 2 }}>
                  <strong>Design 1</strong> uses the main SVG path field above. <strong>Design 2, 3, etc.</strong> use the fields below.
                </Typography>

                {/* Show additional design inputs (Design 2, 3, etc.) - Design 1 uses main svgPath */}
                {Array.from({ length: Math.max(0, (stickerData?.setQuantity || 1) - 1) }).map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <TextField
                      fullWidth
                      label={`Design ${index + 2} Title`}
                      placeholder={`e.g., Design ${index + 2}`}
                      value={stickerData?.svgTitles?.[index] || ''}
                      onChange={(e) => {
                        if (stickerData) {
                          const newTitles = [...(stickerData.svgTitles || [])];
                          newTitles[index] = e.target.value;
                          setStickerData({
                            ...stickerData,
                            svgTitles: newTitles
                          });
                        }
                      }}
                      sx={{
                        minWidth: '200px',
                        '& .MuiInputLabel-root': { color: '#8b949e' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#64b5f6' },
                          '&.Mui-focused fieldset': { borderColor: '#64b5f6' }
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' }
                      }}
                    />
                    <TextField
                      fullWidth
                      placeholder={`Path to design ${index + 2} file`}
                      variant="outlined"
                      value={stickerData?.svgPaths?.[index] || ''}
                      onChange={(e) => {
                        if (stickerData) {
                          const newPaths = [...(stickerData.svgPaths || [])];
                          newPaths[index] = e.target.value;
                          setStickerData({
                            ...stickerData,
                            svgPaths: newPaths
                          });
                        }
                      }}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#8b949e' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#30363d' },
                          '&:hover fieldset': { borderColor: '#64b5f6' },
                          '&.Mui-focused fieldset': { borderColor: '#64b5f6' }
                        },
                        '& .MuiInputBase-input': { color: '#f0f6fc' }
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilePickerTarget({ type: 'design', index });
                        setFileBrowserOpen(true);
                      }}
                      sx={{
                        minWidth: '100px',
                        color: '#f0f6fc',
                        borderColor: '#30363d',
                        '&:hover': {
                          borderColor: '#64b5f6',
                          backgroundColor: 'rgba(100, 181, 246, 0.1)'
                        }
                      }}
                    >
                      Browse
                    </Button>
                    <IconButton
                      onClick={() => {
                        if (stickerData) {
                          const newPaths = [...(stickerData.svgPaths || [])];
                          const newTitles = [...(stickerData.svgTitles || [])];
                          newPaths.splice(index, 1);
                          newTitles.splice(index, 1);
                          setStickerData({
                            ...stickerData,
                            svgPaths: newPaths,
                            svgTitles: newTitles,
                            setQuantity: Math.max(1, (stickerData.setQuantity || 1) - 1)
                          });
                        }
                      }}
                      sx={{
                        color: '#f85149',
                        '&:hover': {
                          backgroundColor: 'rgba(248, 81, 73, 0.1)'
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ))}
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* SVG Path Section */}
        <SvgPathSection
          stickerData={stickerData}
          setStickerData={setStickerData}
          listingData={currentListing}
          onSave={handleSvgPathSave}
          showFileBrowser={true}
          title="Scene Builder File"
          suggestedPath={suggestedSvgPath}
        />

        {/* Sizes Section */}
        <SizesSection
          sizes={sizes}
          setSizes={setSizes}
          listingData={currentListing}
          extractedVariations={extractedVariations}
          onSave={handleSizesSave}
          title="Description - Sizes"
        />

        {/* Progress Section - Moved to bottom */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#21262d', border: '1px solid #30363d' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#f0f6fc' }}>
            Progress
          </Typography>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(progress.completed + progress.skipped) / progress.total * 100}
              sx={{
                mb: 1,
                backgroundColor: '#30363d',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#64b5f6'
                }
              }}
            />
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {progress.completed + progress.skipped} of {progress.total} listings processed
              ({progress.completed} completed, {progress.skipped} skipped)
            </Typography>
          </Box>
        </Paper>

        {/* Navigation Controls */}
        <Paper sx={{ p: 3, mt: 3, backgroundColor: '#21262d', border: '1px solid #30363d' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#f0f6fc' }}>
            Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrevious}
              disabled={progress.completed + progress.skipped === 0}
              sx={{
                color: '#f0f6fc',
                borderColor: '#30363d',
                '&:hover': {
                  borderColor: '#64b5f6',
                  backgroundColor: 'rgba(100, 181, 246, 0.1)'
                },
                '&:disabled': {
                  color: '#8b949e',
                  borderColor: '#30363d'
                }
              }}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleNext}
              sx={{
                backgroundColor: '#238636',
                color: '#f0f6fc',
                '&:hover': {
                  backgroundColor: '#2ea043'
                }
              }}
            >
              Approve & Next
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleNext}
              sx={{
                backgroundColor: '#64b5f6',
                color: '#0d1117',
                '&:hover': {
                  backgroundColor: '#42a5f5'
                }
              }}
            >
              Edit & Next
            </Button>
            <Button
              variant="outlined"
              startIcon={<SkipNextIcon />}
              onClick={handleSkip}
              sx={{
                color: '#f0f6fc',
                borderColor: '#30363d',
                '&:hover': {
                  borderColor: '#64b5f6',
                  backgroundColor: 'rgba(100, 181, 246, 0.1)'
                }
              }}
            >
              Skip for Now
            </Button>
          </Box>
        </Paper>

        {/* File Browser */}
        <SimpleFileBrowser
          open={fileBrowserOpen}
          onClose={() => setFileBrowserOpen(false)}
          onFileSelect={(filePath) => {
            logger.log('Selected file path:', filePath);
            if (stickerData) {
              if (filePickerTarget.type === 'design' && filePickerTarget.index != null) {
                const newPaths = [...(stickerData.svgPaths || [])];
                newPaths[filePickerTarget.index] = filePath;
                setStickerData({ ...stickerData, svgPaths: newPaths });
              } else {
                setStickerData({ ...stickerData, svgPath: filePath });
              }
            }
          }}
        />
      </Container>
    </ThemeProvider>
  );
};

// SvgPathAutoAssociation has no props - it's a standalone admin page component
SvgPathAutoAssociation.propTypes = {};

export default SvgPathAutoAssociation;
